import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, getPortal } from './client';
import { useSession } from '../auth/session';

/**
 * A-08…A-10 — the catalog authoring read models and their write paths.
 *
 * Request bodies are typed by `@miraki/contracts` (`packages/contracts/src/catalog.ts`),
 * which is frozen and shared with the NestJS route. The response shapes below are
 * declared here for the same reason the rest of `types.ts` declares its own: a list
 * row and a detail record are projections chosen per screen, not schemas.
 *
 * **Every price and every weight is a decimal string, end to end.** NUMERIC(19,4) in
 * the database, string on the wire, string in this file, string in the component.
 * Nothing here calls `Number()` on one, and nothing downstream may either (NF-04).
 *
 * **There is no quantity, availability, stock or lead-time field in any shape below,
 * and there is no minimum order quantity.** Every wholesale order is made to order
 * (D4); MOQ was removed by CR-07. If one looks missing, it has been misread — the
 * frozen contract says the same thing in as many words.
 */

// ------------------------------------------------------------------ read models

/** One row of A-08. `codePrefix` is the product's identity; variants extend it. */
export interface ProductRow {
  id: string;
  /** AUR-RG. Displayed, never edited after creation — variants are built from it. */
  codePrefix: string;
  name: string;
  collection: string | null;
  category: string | null;
  /** The trade label, not the code: 'Gold', not 'Au'. */
  metalType: string | null;
  variantCount: number;
  /** Up to six photographs, in display order. Empty where the product has none. */
  imageUrls: string[];
  /** Lowest variant list price. A decimal string, or null where no variant is priced. */
  priceFrom: string | null;
  /** Who created the product, from its audit trail; null when none was recorded. */
  createdBy?: string | null;
  /**
   * PR-02 — the lowest variant RP, beside the lowest wholesale price.
   *
   * Derived independently, not as 3x `priceFrom`: an Admin override (D59) breaks
   * the relationship, and the cheapest variant by wholesale need not be the
   * cheapest by RP.
   */
  rpFrom: string | null;
  currency: string;
  isPublished: boolean;
}

/** One variant of A-09's matrix, as the catalog holds it. */
export interface VariantRow {
  id: string;
  /** Server-derived from prefix, purity, colour and carat (plus Stone, once set). Displayed, never edited. */
  code: string;
  purityValueId: string | null;
  purity: string | null;
  metalColourValueId: string | null;
  metalColour: string | null;
  caratCode: string | null;
  /**
   * Stone — a variant-defining axis, the same standing as purity and metal
   * colour above. `gemstoneValueId` is the MATERIAL (Emerald, Ruby, Sapphire,
   * Diamond — attribute key `gemtype`, deliberately distinct from `shape`'s
   * own 'EM' = emerald-cut). The other four point at the same global trade
   * lists the product-level stone panel already uses for a range; here they
   * name one exact point in it, for this priced, sellable piece.
   */
  /**
   * Lab Grown or Natural (attribute key `origin`) — an ORIGIN, orthogonal to
   * the material beside it, and PRICED rather than descriptive: three of the
   * client's six catalogue files ship both values and the origin moves the
   * price in every one. Optional here only because the route that returns it
   * is landing in a parallel change; `zCreateVariant.stoneOriginValueId` is
   * already frozen.
   */
  stoneOriginValueId?: string | null;
  stoneOrigin?: string | null;
  gemstoneValueId: string | null;
  gemstone: string | null;
  stoneShapeValueId: string | null;
  stoneShape: string | null;
  stoneCutValueId: string | null;
  stoneCut: string | null;
  stoneColourValueId: string | null;
  stoneColour: string | null;
  stoneClarityValueId: string | null;
  stoneClarity: string | null;
  netMetalWeightG: string | null;
  grossWeightG: string | null;
  totalCaratWeight: string | null;
  listPrice: string;
  /**
   * PR-02 — the RP. Defaults to 3x the wholesale price on write (PR-05) and is
   * overridable per variant (D59), which is what the inline editor is for.
   *
   * Nullable only for a variant written before migration 0032 backfilled it, so
   * render a dash rather than assume a figure. PR-03 — the retailer is invoiced
   * on `listPrice`, never on this.
   */
  rpPrice: string | null;
  isActive: boolean;
  attributes: Record<string, unknown>;
}

/** A-09's record. Mirrors `zCreateProduct` plus the identifiers a form cannot invent. */
export interface ProductDetail {
  id: string;
  codePrefix: string;
  name: string;
  description: string | null;
  collectionId: string | null;
  collection: string | null;
  categoryId: string | null;
  category: string | null;
  metalTypeCode: string;
  metalType: string | null;
  hsCode: string | null;
  countryOfOrigin: string | null;
  settingValueId: string | null;
  finishValueId: string | null;
  claspValueId: string | null;
  backingValueId: string | null;
  hallmarkValueId: string | null;
  isPublished: boolean;
  currency: string;
  /**
   * Every value of every axis this design is offered in — Purity, Metal
   * colour, Carat, and Stone's Material/Shape/Cut/Colour/Clarity — as one
   * flat list of attribute-value ids. The screen buckets it by attribute key
   * itself, the same way it already does for `variants`' stone ids.
   */
  offeredValueIds: string[];
  variants: VariantRow[];
  images: ProductImage[];
  /**
   * CR-43 — the stones the design carries and the rules that decide when a
   * grading report travels with a piece. Both arrive with the product and both
   * are replaced wholesale by their own PUT; an empty array clears a set.
   *
   * Optional, because the routes that fill them are landing in a parallel
   * change and a product editor that throws on `undefined.map` is worse than
   * one that shows an empty panel for an afternoon.
   */
  stones?: ProductStoneRow[];
  certificateRules?: CertificateRuleRow[];
}

/**
 * CR-43 — one stone the design carries. A piece holds several and they are
 * rarely alike: a certified centre and twelve uncertified shoulders.
 *
 * The four grading fields are a **range the piece is built to** — '0.30 - 1.00',
 * '0.005 each' — which is why they are free text and not attribute ids. The
 * exact four Cs of one sellable piece live on the variant instead.
 */
export interface ProductStoneRow {
  id: string;
  /** Centre, Shoulder, Accent. */
  position: string;
  shapeValueId: string | null;
  shapeLabel: string | null;
  stoneCount: number;
  caratSpec: string | null;
  cutSpec: string | null;
  colourSpec: string | null;
  claritySpec: string | null;
  isCertified: boolean;
}

/**
 * CR-43 — the RULE that triggers a certificate. Never the certificate.
 *
 * There is no file, no document and no attachment in this shape, and there must
 * not be one: a grading report is written about ONE physical stone, and a
 * variant is a specification sold many times. The document belongs to the piece
 * that gets manufactured, and whether a piece even carries an identity of its
 * own is CR-47 — still an open client question.
 */
export interface CertificateRuleRow {
  id: string;
  body: string;
  appliesWhen: string;
  scope: string;
}

export type CollectionState = 'DRAFT' | 'PUBLISHED';

export interface CollectionRow {
  id: string;
  name: string;
  slug: string;
  state: CollectionState;
  sortOrder: number;
  variantCount: number;
}

export interface CategoryRow {
  id: string;
  name: string;
  slug: string;
  sortOrder: number;
  variantCount: number;
}

/**
 * CR-12 — one option list, with the published standard it follows.
 *
 * `isActive` is the portal's answer, not a global one: the API resolves it for the
 * portal in context, which is why this screen never has to ask which portal it is on.
 * A deactivated value is not deleted — pieces already carrying it keep it, and it
 * stops being offered from here on.
 */
export interface AttributeValue {
  id: string;
  code: string;
  label: string;
  /** The trade standard the value comes from, where there is one. */
  standard: string | null;
  isActive: boolean;
  /** CR-45 — karat is a gold measure. Null means the value applies to any metal. */
  metalTypeCode: string | null;
}

export interface ProductImage {
  id: string;
  /**
   * Derived server-side from the stored content type, not held as a column —
   * `mki_product_images` stores a path and never bytes, so a video row needed no
   * schema change to become expressible.
   */
  kind: 'IMAGE' | 'VIDEO';
  contentType: string | null;
  url: string;
  caption: string | null;
  sortOrder: number;
  /**
   * The one photograph that leads everywhere — catalog card, cart line, and the
   * first frame of the retailer's rail. One per product across every colour;
   * `sortOrder` only orders images within a colour and could never say this.
   */
  isPrimary: boolean;
  /** 'Y' | 'W' | 'R', or null where the metal has no colour axis. */
  metalColour: string | null;
  metalColourLabel: string | null;
}

export interface AttributeGroup {
  /** 'purity', 'mcol', 'setting', 'finish', 'clasp', 'backing', 'hallmark', … */
  key: string;
  name: string;
  standard: string;
  values: AttributeValue[];
}

// ------------------------------------------------------------------- filtering

export interface ProductQuery {
  search?: string | undefined;
  collectionId?: string | undefined;
  categoryId?: string | undefined;
  /** 1-based. Sent together with `pageSize` — the server pages only when asked to. */
  page?: number | undefined;
  pageSize?: number | undefined;
}

/** One page of A-08, and how many products match in all — the count is what the pager needs. */
export interface ProductPage {
  items: ProductRow[];
  total: number;
  page: number;
  pageSize: number;
}

function productQuery(q: ProductQuery): string {
  const qs = new URLSearchParams();
  if (q.search) qs.set('search', q.search);
  if (q.collectionId) qs.set('collectionId', q.collectionId);
  if (q.categoryId) qs.set('categoryId', q.categoryId);
  if (q.page) qs.set('page', String(q.page));
  if (q.pageSize) qs.set('pageSize', String(q.pageSize));
  const s = qs.toString();
  return s ? `?${s}` : '';
}

// ----------------------------------------------------------------------- scope

/**
 * The portal is part of every catalog cache key.
 *
 * Read from the session rather than from module state, for the reason `hooks.ts`
 * records at length: the effect that publishes a new portal runs after the render
 * that computes the key, so module state hands back the previous portal's cache
 * entry under the new portal's label. That is the SC-02 boundary leaking through
 * the client, and a catalog is exactly where it would be least visible.
 */
function useScope() {
  const { portal } = useSession();
  return (key: unknown[]) => ['portal', portal?.code ?? null, 'catalog', ...key];
}

// ----------------------------------------------------------------------- reads

/**
 * One page of products. `keepPreviousData`: moving to the next page keeps the
 * current rows on screen until the new ones land, rather than swapping the whole
 * table for a spinner on every click — `isPlaceholderData` says when that is
 * what is being shown.
 */
export const useProducts = (q: ProductQuery = {}) => {
  const scoped = useScope();
  const suffix = productQuery(q);
  return useQuery({
    queryKey: scoped(['products', suffix]),
    queryFn: () => api.get<ProductPage>(`/catalog/products${suffix}`),
    placeholderData: keepPreviousData,
  });
};

export const useProduct = (code: string | undefined) => {
  const scoped = useScope();
  return useQuery({
    queryKey: scoped(['product', code]),
    queryFn: () => api.get<ProductDetail>(`/catalog/products/${code}`),
    enabled: !!code,
  });
};

export const useVariants = (productId: string | undefined) => {
  const scoped = useScope();
  return useQuery({
    queryKey: scoped(['variants', productId]),
    queryFn: () => api.get<VariantRow[]>(`/catalog/products/${productId}/variants`),
    enabled: !!productId,
  });
};

export const useCollections = () => {
  const scoped = useScope();
  return useQuery({
    queryKey: scoped(['collections']),
    queryFn: () => api.get<CollectionRow[]>('/catalog/collections'),
  });
};

export const useCategories = () => {
  const scoped = useScope();
  return useQuery({
    queryKey: scoped(['categories']),
    queryFn: () => api.get<CategoryRow[]>('/catalog/categories'),
  });
};

/**
 * The values this portal offers, with the active flag already resolved for it.
 *
 * The product editor filters on `isActive` before it offers anything: a draft
 * written against a value that was since switched off must not be able to add
 * more of it. Publishing is checked again server-side at the moment of publishing,
 * because a save-time check alone would miss a value deactivated in between.
 */
/**
 * The API serves this flat, one row per value, as `/catalog/attribute-values` —
 * it exists to answer "what may the editor offer here", so it returns only the
 * values ACTIVE for the portal in context and says nothing about the inactive
 * ones. Grouping is done here rather than server-side because the grouping is a
 * presentation choice and the activation is not.
 *
 * Consequence worth knowing: `isActive` is always true in what comes back, and
 * A-10 therefore cannot currently show or change a value that is switched OFF.
 * That needs an admin-facing endpoint the API does not yet have — see
 * `useSetAttributeValueActive` below.
 */
interface FlatAttributeValue {
  id: string;
  attributeKey: string;
  attributeTitle: string;
  code: string;
  label: string;
  note: string | null;
}

export const useAttributes = () => {
  const scoped = useScope();
  return useQuery({
    queryKey: scoped(['attributes']),
    queryFn: async (): Promise<AttributeGroup[]> => {
      const flat = await api.get<FlatAttributeValue[]>('/catalog/attribute-values');
      const groups = new Map<string, AttributeGroup>();
      for (const v of flat) {
        let g = groups.get(v.attributeKey);
        if (!g) {
          g = { key: v.attributeKey, name: v.attributeTitle, standard: '', values: [] };
          groups.set(v.attributeKey, g);
        }
        g.values.push({
          id: v.id,
          code: v.code,
          label: v.label,
          standard: v.note,
          // Only active values are served by this endpoint, so this is not a claim
          // that nothing is switched off — it is all this endpoint can see.
          isActive: true,
          metalTypeCode: null,
        });
      }
      return [...groups.values()];
    },
  });
};

// ---------------------------------------------------------------------- writes

/**
 * Every catalog mutation invalidates the audit log alongside its own resource, for
 * the reason `hooks.ts` gives: a change that does not appear in the log on the next
 * screen looks like it was not recorded.
 */
function useInvalidating<TArgs, TResult>(
  fn: (args: TArgs) => Promise<TResult>,
  makeKeys: (scoped: (key: unknown[]) => unknown[]) => unknown[][],
) {
  const qc = useQueryClient();
  const scoped = useScope();
  const { portal } = useSession();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      const keys = [...makeKeys(scoped), ['portal', portal?.code ?? null, 'audit']];
      for (const k of keys) void qc.invalidateQueries({ queryKey: k });
    },
  });
}

const PRODUCT_KEYS = (scoped: (key: unknown[]) => unknown[]) => [
  scoped(['products']), scoped(['product']), scoped(['variants']),
];

export const useCreateProduct = () =>
  useInvalidating((body: unknown) => api.post<ProductDetail>('/catalog/products', body), PRODUCT_KEYS);

export const useUpdateProduct = () =>
  useInvalidating(
    ({ id, body }: { id: string; body: unknown }) => api.patch<unknown>(`/catalog/products/${id}`, body),
    PRODUCT_KEYS,
  );

/**
 * Separate from saving on purpose — see the publishing note in the frozen contract.
 *
 * Cascades to the product's own variants server-side, the same as A-08's bulk
 * action — a retailer sees a variant only when BOTH the product and the variant
 * are published, so `variantsChanged` is what tells this screen the toggle did
 * more than flip one flag nobody can see.
 */
export const useSetProductPublished = () =>
  useInvalidating(
    ({ id, isPublished }: { id: string; isPublished: boolean }) =>
      api.post<{ variantsChanged: number }>(`/catalog/products/${id}/publish`, { isPublished }),
    PRODUCT_KEYS,
  );

/**
 * `code` is not in the body. The server derives it from the product prefix, the
 * purity, the metal colour and the carat, and `UNIQUE (portal_id, code)` enforces
 * the rest. A hand-typed code is how two variants end up meaning the same thing.
 */
export const useCreateVariant = () =>
  useInvalidating(
    ({ productId, body }: { productId: string; body: unknown }) =>
      api.post<VariantRow>('/catalog/variants', { ...(body as object), productId }),
    PRODUCT_KEYS,
  );

/**
 * Patch the cached product's `variants` array in place with a row the server
 * already sent back, instead of marking the whole product stale and waiting
 * on a second round trip for it — a save to one variant's price or weight
 * does not need every variant, every image and every stone back over the
 * network again. Matched by key PREFIX (`scoped(['product'])`, not a specific
 * code) because the mutation only knows the variant's id, not which cached
 * product page — if any — currently holds it; in practice at most one does.
 */
function patchVariantInCache(
  qc: ReturnType<typeof useQueryClient>, scoped: (key: unknown[]) => unknown[],
  variantId: string, patch: Partial<VariantRow>,
) {
  qc.setQueriesData<ProductDetail>({ queryKey: scoped(['product']) }, (old) => {
    if (!old) return old;
    return { ...old, variants: old.variants.map((v) => (v.id === variantId ? { ...v, ...patch } : v)) };
  });
}

export const useUpdateVariant = () => {
  const qc = useQueryClient();
  const scoped = useScope();
  const { portal } = useSession();
  return useMutation({
    mutationFn: ({ variantId, body }: { productId?: string; variantId: string; body: unknown }) =>
      api.patch<Partial<VariantRow>>(`/catalog/variants/${variantId}`, body),
    onSuccess: (updated, { variantId }) => {
      patchVariantInCache(qc, scoped, variantId, updated);
      // `products` (the list) still needs a real refetch — list price feeds
      // its `priceFrom`, which this response alone cannot recompute — but
      // the product DETAIL query is already correct from the line above, so
      // it is deliberately not in this list.
      void qc.invalidateQueries({ queryKey: scoped(['products']) });
      void qc.invalidateQueries({ queryKey: ['portal', portal?.code ?? null, 'audit'] });
    },
  });
};

export const useSetVariantActive = () => {
  const qc = useQueryClient();
  const scoped = useScope();
  const { portal } = useSession();
  return useMutation({
    mutationFn: ({ variantId, isActive }: { productId?: string; variantId: string; isActive: boolean }) =>
      api.post<Partial<VariantRow>>(`/catalog/variants/${variantId}/active`, { isActive }),
    onSuccess: (updated, { variantId, isActive }) => {
      // The route's own response carries `isActive`, but the body sent IS
      // the answer either way — patch with it directly rather than trust a
      // response shape that could, in principle, omit the field.
      patchVariantInCache(qc, scoped, variantId, { ...updated, isActive });
      // Withdrawing the cheapest active variant moves `priceFrom` on the
      // list; the product detail query is already correct above.
      void qc.invalidateQueries({ queryKey: scoped(['products']) });
      void qc.invalidateQueries({ queryKey: ['portal', portal?.code ?? null, 'audit'] });
    },
  });
};

/**
 * Withdraw, redefined: a genuine removal, not `isActive: false`. The row is
 * dropped from the cached product's `variants` array with the same
 * key-prefix match `patchVariantInCache` uses, rather than waiting on a
 * refetch to notice it is gone.
 */
export const useDeleteVariant = () => {
  const qc = useQueryClient();
  const scoped = useScope();
  const { portal } = useSession();
  return useMutation({
    mutationFn: ({ variantId }: { productId?: string; variantId: string }) =>
      api.del<unknown>(`/catalog/variants/${variantId}`),
    onSuccess: (_result, { variantId }) => {
      qc.setQueriesData<ProductDetail>({ queryKey: scoped(['product']) }, (old) => {
        if (!old) return old;
        return { ...old, variants: old.variants.filter((v) => v.id !== variantId) };
      });
      // Removing the cheapest variant moves `priceFrom` on the list; the
      // product detail query is already correct above.
      void qc.invalidateQueries({ queryKey: scoped(['products']) });
      void qc.invalidateQueries({ queryKey: ['portal', portal?.code ?? null, 'audit'] });
    },
  });
};

export interface BulkDeleteVariantsResult {
  deleted: number;
  refused: Array<{ id: string; reason: string }>;
}

/**
 * Withdraw a whole group in ONE request — a group selected in the Variance
 * block can be dozens of variants, and looping the single-variant route
 * meant one HTTP round trip per row for what is really one action. Every
 * requested id that was NOT refused is dropped from the cached product's
 * `variants` array in one update, the same as `useDeleteVariant` does for
 * one row.
 */
export const useDeleteVariants = () => {
  const qc = useQueryClient();
  const scoped = useScope();
  const { portal } = useSession();
  return useMutation({
    mutationFn: ({ ids }: { productId?: string; ids: string[] }) =>
      api.post<BulkDeleteVariantsResult>('/catalog/variants/delete', { ids }),
    onSuccess: (result, { ids }) => {
      const refusedIds = new Set(result.refused.map((r) => r.id));
      const removed = new Set(ids.filter((id) => !refusedIds.has(id)));
      qc.setQueriesData<ProductDetail>({ queryKey: scoped(['product']) }, (old) => {
        if (!old) return old;
        return { ...old, variants: old.variants.filter((v) => !removed.has(v.id)) };
      });
      void qc.invalidateQueries({ queryKey: scoped(['products']) });
      void qc.invalidateQueries({ queryKey: ['portal', portal?.code ?? null, 'audit'] });
    },
  });
};

const TAXONOMY_KEYS = (scoped: (key: unknown[]) => unknown[]) => [
  scoped(['collections']), scoped(['categories']), scoped(['products']),
];

export const useCreateCollection = () =>
  useInvalidating((body: unknown) => api.post<unknown>('/catalog/collections', body), TAXONOMY_KEYS);

export const useUpdateCollection = () =>
  useInvalidating(
    ({ id, body }: { id: string; body: unknown }) => api.patch<unknown>(`/catalog/collections/${id}`, body),
    TAXONOMY_KEYS,
  );

export const useCreateCategory = () =>
  useInvalidating((body: unknown) => api.post<unknown>('/catalog/categories', body), TAXONOMY_KEYS);

export const useUpdateCategory = () =>
  useInvalidating(
    ({ id, body }: { id: string; body: unknown }) => api.patch<unknown>(`/catalog/categories/${id}`, body),
    TAXONOMY_KEYS,
  );

/**
 * CR-12 — one switch governs three places: the bulk upload rejects an inactive
 * value, the product editor stops offering it, and the customer never sees it.
 * Nothing is deleted, so the switch is reversible.
 */
export const useSetAttributeValueActive = () =>
  useInvalidating(
    ({ valueId, isActive }: { valueId: string; isActive: boolean }) =>
      api.post<unknown>(`/catalog/attributes/values/${valueId}/active`, { isActive }),
    (scoped) => [scoped(['attributes']), scoped(['product']), scoped(['products'])],
  );

/**
 * Removing a photograph removes the ROW. The file itself is shipped with the web
 * app, so there is nothing to unlink — and once storage exists, an object other
 * rows may still point at must not be deleted because one row stopped.
 */
export const useDeleteProductImage = () =>
  useInvalidating(
    ({ productId, imageId }: { productId: string; imageId: string }) =>
      api.del<unknown>(`/catalog/products/${productId}/images/${imageId}`),
    PRODUCT_KEYS,
  );

/**
 * Mark the one photograph that represents a design.
 *
 * The server clears the previous primary and sets this one inside a single
 * transaction, because `mki_product_images_one_primary` forbids two at once —
 * so this is one call, not a clear followed by a set.
 */
/**
 * Optimistic, because the result is deterministic before the request even
 * lands — this image becomes primary, every other one on the product stops
 * being. Waiting on a full product refetch to show that was the actual
 * slowness (every variant, every image, every stone, again), not the write
 * itself, which flips two rows behind one partial unique index.
 */
export const useSetPrimaryProductImage = () => {
  const qc = useQueryClient();
  const scoped = useScope();
  const { portal } = useSession();
  const productKey = scoped(['product']);
  return useMutation({
    mutationFn: ({ productId, imageId }: { productId: string; imageId: string }) =>
      api.put<unknown>(`/catalog/products/${productId}/images/${imageId}/primary`, {}),
    onMutate: async ({ imageId }) => {
      // Cancelled so an in-flight GET cannot land after the optimistic write
      // and overwrite it with what the server held a moment ago.
      await qc.cancelQueries({ queryKey: productKey });
      const previous = qc.getQueriesData<ProductDetail>({ queryKey: productKey });
      qc.setQueriesData<ProductDetail>({ queryKey: productKey }, (old) => {
        if (!old) return old;
        return { ...old, images: old.images.map((img) => ({ ...img, isPrimary: img.id === imageId })) };
      });
      return { previous };
    },
    // The failed guess is undone with the exact snapshot taken before it,
    // not a refetch — a refetch here would be the same slow round trip this
    // was written to avoid, on the one path (failure) that needs to be right.
    onError: (_err, _vars, context) => {
      context?.previous.forEach(([key, data]) => qc.setQueryData(key, data));
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['portal', portal?.code ?? null, 'audit'] });
    },
  });
};

/**
 * Upload a photograph. The body is the file itself, so this does not go through
 * `api.post`, which sends JSON — the colour and caption ride in the query string
 * because they are two short strings and the file is the only part that is a file.
 */
export const useAddProductImage = () =>
  useInvalidating(
    async ({ productId, file, colour, caption }: {
      productId: string; file: File; colour: string | null; caption: string;
    }) => {
      const q = new URLSearchParams();
      if (colour) q.set('colour', colour);
      if (caption) q.set('caption', caption);
      const res = await fetch(`/api/catalog/products/${productId}/images?${q}`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': file.type },
        body: file,
      });
      if (!res.ok) {
        const text = await res.text();
        let message = `Upload failed (${res.status})`;
        try { message = (JSON.parse(text) as { message?: string }).message ?? message; } catch { /* not json */ }
        throw new Error(message);
      }
      return res.json() as Promise<ProductImage>;
    },
    PRODUCT_KEYS,
  );

/**
 * Publish or withdraw several products at once — A-08's bulk action.
 *
 * It cascades to variants, because a retailer sees a variant only when BOTH the
 * product and the variant are published. Setting the product flag alone would
 * change nothing a customer could see, which is not what pressing Publish means.
 */
export const useBulkPublish = () =>
  useInvalidating(
    ({ ids, isPublished }: { ids: string[]; isPublished: boolean }) =>
      api.post<{ changed: number; variantsChanged: number; refused: { id: string; reason: string }[] }>(
        '/catalog/products/publish', { ids, isPublished },
      ),
    PRODUCT_KEYS,
  );

export interface BulkDeleteProductsResult {
  deleted: number;
  refused: Array<{ id: string; reason: string }>;
}

/**
 * A-08's Withdraw — one request for the whole selection, a genuine removal
 * (`CatalogService.deleteProducts`), not `isPublished: false`. Every
 * requested id that was NOT refused is dropped from every cached products
 * list in one update, the same as `useDeleteVariants` does for a product's
 * own variant array — that alone makes the row vanish the instant this
 * resolves, with no round trip to wait for.
 *
 * `invalidateQueries` runs alongside it rather than instead of it, on
 * purpose: the patch above is this screen's own best effort at the new
 * state, and invalidating marks it stale so the NEXT time this list is
 * read — a refetch, a remount, a different filter — it is asked for again
 * rather than trusted forever. Without this, a product removed here could
 * reappear the moment something else (a focus refetch, a stale cached
 * variant of the same query under a different filter) served the list from
 * a snapshot taken before the delete.
 */
export const useDeleteProducts = () => {
  const qc = useQueryClient();
  const scoped = useScope();
  const { portal } = useSession();
  return useMutation({
    mutationFn: ({ ids }: { ids: string[] }) =>
      api.post<BulkDeleteProductsResult>('/catalog/products/delete', { ids }),
    onSuccess: (result, { ids }) => {
      const refusedIds = new Set(result.refused.map((r) => r.id));
      const removed = new Set(ids.filter((id) => !refusedIds.has(id)));
      /*
       * `useProducts` caches a ProductPage — { items, total, page, pageSize } — not an array.
       * This updated it as ProductRow[] and called `old.filter`, which threw "old.filter is not
       * a function" the moment a Withdraw succeeded. Two things hid it: `setQueriesData` matches
       * by PREFIX, so every products query reaches this updater whatever shape it holds, and the
       * throw lands AFTER the server has already deleted — the catalogue changed and the screen
       * reported failure. Found by TS2's browser suite, which is where a thrown updater shows up
       * and a unit test does not.
       *
       * `total` is decremented with it: leaving it would have the pager offer a page that is no
       * longer there, and that count is the first thing anyone reads after a bulk action.
       */
      qc.setQueriesData<ProductPage>(
        { queryKey: scoped(['products']) },
        (old) => {
          if (!old || !Array.isArray(old.items)) return old;
          const items = old.items.filter((p) => !removed.has(p.id));
          return { ...old, items, total: Math.max(0, old.total - (old.items.length - items.length)) };
        },
      );
      void qc.invalidateQueries({ queryKey: scoped(['products']) });
      void qc.invalidateQueries({ queryKey: ['portal', portal?.code ?? null, 'audit'] });
    },
  });
};

// -------------------------------------------------- A-09 stones & certificates

/**
 * Request bodies, declared here rather than imported from `@miraki/contracts`.
 *
 * `z.infer` gives a schema's OUTPUT type, and `zMoney` transforms a string into
 * a `Money` decimal — so `BulkVariant['listPrice']` is `Money`, which is what
 * the ROUTE receives after parsing and not what the wire carries. A request body
 * typed from it would be a lie in the one place this codebase least tolerates
 * one. These mirror the schemas' INPUT side: money is a string, always (NF-04).
 */
export interface ProductStoneBody {
  position: string;
  shapeValueId: string | null;
  /** A count of stones. An integer, and the only integer on this path. */
  stoneCount: number;
  caratSpec: string | null;
  cutSpec: string | null;
  colourSpec: string | null;
  claritySpec: string | null;
  isCertified: boolean;
}

/**
 * A certification, as saved. `body` is the certification body (GIA or IGI),
 * `appliesWhen` holds the VARIANT CODE and `scope` the CERTIFICATE NUMBER — the
 * column names predate what they now carry (see `zCertificateRule`). `id` is sent
 * back on every save so a certification keeps the documents attached to it.
 */
export interface CertificateRuleBody {
  id?: string;
  body: string;
  appliesWhen: string;
  scope: string;
}

/** Replaces the whole set. An empty array clears it — `zProductStone`, frozen. */
export const useSetProductStones = () =>
  useInvalidating(
    ({ productId, stones }: { productId: string; stones: ProductStoneBody[] }) =>
      api.put<{ stones: ProductStoneRow[] }>(`/catalog/products/${productId}/stones`, { stones }),
    PRODUCT_KEYS,
  );

/** One document attached to a certification. Metadata only — the bytes are fetched on demand. */
export interface CertificateAttachment {
  id: string;
  certificateId: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  createdAt: string;
}

export const useCertificateAttachments = (productId: string | undefined) => {
  const scoped = useScope();
  return useQuery({
    queryKey: scoped(['certificate-attachments', productId]),
    queryFn: () =>
      api.get<CertificateAttachment[]>(`/catalog/products/${productId}/certificate-attachments`),
    enabled: !!productId,
  });
};

const ATTACHMENT_KEYS = (scoped: (key: unknown[]) => unknown[]) => [scoped(['certificate-attachments'])];

/**
 * One document per call, the raw file as the body — the same shape photographs
 * use. The portal header is added by hand because this does not go through the
 * JSON client.
 */
export const useAddCertificateAttachment = () =>
  useInvalidating(
    async ({ productId, certificateId, file }: { productId: string; certificateId: string; file: File }) => {
      const portal = getPortal();
      const res = await fetch(
        `/api/catalog/products/${productId}/certificate-rules/${certificateId}/attachments?${new URLSearchParams({ name: file.name })}`,
        {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'content-type': file.type, ...(portal ? { 'x-portal': portal } : {}) },
          body: file,
        },
      );
      if (!res.ok) {
        const text = await res.text();
        let message = `Upload failed (${res.status})`;
        try { message = (JSON.parse(text) as { message?: string }).message ?? message; } catch { /* not json */ }
        throw new Error(message);
      }
      return res.json() as Promise<CertificateAttachment>;
    },
    ATTACHMENT_KEYS,
  );

export const useDeleteCertificateAttachment = () =>
  useInvalidating(
    ({ productId, attachmentId }: { productId: string; attachmentId: string }) =>
      api.del<unknown>(`/catalog/products/${productId}/certificate-attachments/${attachmentId}`),
    ATTACHMENT_KEYS,
  );

/**
 * Open a certificate document in a new tab. A plain link cannot carry the
 * `x-portal` header, and the document is scoped to the portal, so it is fetched
 * with the header and shown from a blob. The tab is opened FIRST, synchronously
 * inside the click, because a tab opened after an await is treated as a popup.
 */
export async function openCertificateAttachment(attachment: CertificateAttachment): Promise<void> {
  const tab = window.open('', '_blank');
  try {
    const portal = getPortal();
    const res = await fetch(`/api/catalog/certificate-attachments/${attachment.id}`, {
      credentials: 'same-origin',
      ...(portal ? { headers: { 'x-portal': portal } } : {}),
    });
    if (!res.ok) throw new Error(`Could not open the document (${res.status})`);
    const url = URL.createObjectURL(await res.blob());
    if (tab) tab.location.href = url; else window.location.assign(url);
    // The tab has taken its own reference by now; release ours later.
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (e) {
    tab?.close();
    throw e;
  }
}

/** Replaces the whole set. An empty array clears it — `zCertificateRule`, frozen. */
export const useSetCertificateRules = () =>
  useInvalidating(
    ({ productId, rules }: { productId: string; rules: CertificateRuleBody[] }) =>
      api.put<{ rules: CertificateRuleRow[] }>(
        `/catalog/products/${productId}/certificate-rules`, { rules },
      ),
    PRODUCT_KEYS,
  );

// ------------------------------------------------------- A-09 saves the matrix

/**
 * `zBulkVariant`, input side. Two things about it decide how A-09's grid saves.
 *
 * 1. **Purity and metal colour travel as CODES, not ids.** The route resolves
 *    them against the portal's active attribute values in memory and refuses a
 *    code that is not active here, by name. So the grid sends `value.code`.
 * 2. **There is no stone column on this path.** `zCreateVariant` has six stone
 *    fields; `zBulkVariant` has none, and the service writes `attributes`
 *    verbatim. Stone identity therefore rides in the metafield seam under the
 *    variant's own field names, so that lifting it into real columns later is a
 *    migration and not a re-import.
 */
export interface BulkVariantBody {
  /** Which product, by its code prefix. */
  productCode: string;
  /**
   * Omit to have the server derive it. Supplied when the business already has a
   * SKU for the piece — and it must be supplied wherever Stone distinguishes two
   * rows, because a derived code is built from prefix, purity, colour and carat
   * alone and would collide. Never derived in the browser.
   */
  code?: string;
  purity: string | null;
  metalColour: string | null;
  caratCode: string | null;
  /** A decimal string, straight from the input. Never a number (NF-04). */
  listPrice: string;
  /** Optional, same as on the single-variant route — a string or omitted, never a number (NF-04). */
  netMetalWeightG?: string | null;
  grossWeightG?: string | null;
  totalCaratWeight?: string | null;
  attributes: Record<string, unknown>;
}

export interface BulkImportBody {
  /** A-09 sends none: the product exists, and only its variants are being created. */
  products?: never[];
  variants: BulkVariantBody[];
  /** BU-04 — run every check and write nothing. */
  dryRun: boolean;
}

export interface BulkRefusal {
  kind: 'product' | 'variant';
  ref: string;
  reason: string;
}

export interface BulkResult {
  dryRun: boolean;
  productsCreated: number;
  productsExisting: number;
  variantsCreated: number;
  variantsExisting: number;
  refused: BulkRefusal[];
  queries: number;
}

/**
 * A-11's route, used by A-09's variant grid — deliberately, rather than a second
 * bulk-create path beside it.
 *
 * Posting a row at a time costs a full request each: session, permission, page
 * and portal resolution, about twenty-one queries, before the route does any
 * work. That is what made 731 rows take sixty-eight minutes. This route resolves
 * the actor once, fetches every lookup table once, validates in memory and
 * writes in one transaction — the same 731 rows in about seven seconds.
 *
 * It is gated on `product.bulk`, NOT `product.edit`: creating two hundred
 * variants in one action is a different act from amending one, and the page
 * catalog already floors A-11 on it.
 */
export const useBulkImport = () =>
  useInvalidating(
    (body: BulkImportBody) => api.post<BulkResult>('/catalog/import', body),
    PRODUCT_KEYS,
  );
